import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Share2, Search, Sparkles, Loader2, RefreshCw, AlertTriangle, Zap, Copy, Check } from 'lucide-react';
import api from '@/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_web-pulse-4/artifacts/i5z1wfjq_WTFH%20logo.png';

const CARD_TABS = [
  { key: 'what', label: 'WHAT', emoji: '\uD83D\uDCCB', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
  { key: 'why', label: 'WHY', emoji: '\uD83E\uDDE0', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  { key: 'you', label: 'YOU', emoji: '\uD83D\uDC64', color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
];

// Simple cache for repeated inputs
const cache = new Map();

export default function SharePage({ onBack }) {
  const [sharedContent, setSharedContent] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeCard, setActiveCard] = useState('what');
  const [copied, setCopied] = useState(false);
  const [source, setSource] = useState(''); // 'share' or 'manual'

  // Extract shared content from URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const title = params.get('title') || '';
    const text = params.get('text') || '';
    const url = params.get('url') || '';

    const parts = [title, text, url].filter(Boolean);
    const combined = parts.join(' ').trim();

    if (combined) {
      setSharedContent(combined);
      setSource('share');
      // Clean URL without losing the page
      window.history.replaceState({}, '', '/share');
      // Auto-explain
      explainContent(combined);
    }
  }, []);

  const explainContent = useCallback(async (content) => {
    if (!content.trim()) return;

    // Check cache
    const cacheKey = content.trim().toLowerCase().slice(0, 200);
    if (cache.has(cacheKey)) {
      setExplanation(cache.get(cacheKey));
      return;
    }

    setLoading(true);
    setError('');
    setExplanation(null);
    setActiveCard('what');

    try {
      const data = await api.explain(content.trim());
      const result = data.explanation || data;
      setExplanation(result);
      cache.set(cacheKey, result);
    } catch (e) {
      console.error('Explain failed:', e);
      setError('Failed to generate explanation. Tap to retry.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualInput.trim() || loading) return;
    setSharedContent(manualInput.trim());
    setSource('manual');
    explainContent(manualInput.trim());
  };

  const handleRetry = () => {
    if (sharedContent) explainContent(sharedContent);
  };

  const handleCopy = async () => {
    if (!explanation) return;
    const text = [
      `WHAT: ${explanation.card_1 || ''}`,
      `WHY: ${explanation.card_2 || ''}`,
      `YOU: ${explanation.card_3 || ''}`,
    ].join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const cardData = explanation ? {
    what: {
      title: 'What Happened',
      content: explanation.card_1 || '',
      detail: explanation.card_1_detail || '',
    },
    why: {
      title: 'Why It Happened',
      content: explanation.card_2 || '',
      detail: explanation.card_2_detail || '',
    },
    you: {
      title: 'Why It Matters to You',
      content: explanation.card_3 || '',
      detail: explanation.card_3_detail || '',
      affects: explanation.card_3_affects || [],
      action: explanation.card_3_action || [],
    },
  } : null;

  return (
    <div data-testid="share-page" className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="flex items-center justify-between px-5 py-3 max-w-lg mx-auto">
          <button
            data-testid="share-back-btn"
            onClick={onBack}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-600" />
          </button>
          <img src={LOGO_URL} alt="WTFHappened" className="h-8 object-contain" />
          <button
            data-testid="share-copy-btn"
            onClick={handleCopy}
            disabled={!explanation}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors disabled:opacity-40"
          >
            {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} className="text-gray-600" />}
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-5 py-6">
        {/* Manual input (always visible when no shared content or after results) */}
        {!loading && (
          <motion.form
            onSubmit={handleManualSubmit}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="flex items-center bg-white rounded-2xl border border-gray-200 
              shadow-sm px-4 py-3 focus-within:border-gray-400 focus-within:shadow-md transition-all">
              <Search size={18} className="text-gray-400 mr-3 shrink-0" />
              <input
                data-testid="share-manual-input"
                type="text"
                value={manualInput}
                onChange={e => setManualInput(e.target.value)}
                placeholder="Paste a link or headline..."
                className="flex-1 bg-transparent outline-none text-base text-gray-900 placeholder:text-gray-400"
                disabled={loading}
              />
              <button
                data-testid="share-explain-btn"
                type="submit"
                disabled={!manualInput.trim() || loading}
                className={`ml-2 px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-1.5 transition-all
                  ${manualInput.trim() && !loading
                    ? 'bg-gray-900 text-white hover:bg-gray-800'
                    : 'bg-gray-100 text-gray-400'}`}
              >
                <Sparkles size={14} /> Explain
              </button>
            </div>
          </motion.form>
        )}

        {/* Shared content badge */}
        {sharedContent && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-5 px-4 py-3 bg-white rounded-2xl border border-gray-100"
          >
            <p className="text-[10px] font-bold tracking-widest text-gray-400 uppercase mb-1">
              {source === 'share' ? 'Shared from your device' : 'Your input'}
            </p>
            <p className="text-sm text-gray-700 line-clamp-3 leading-relaxed">{sharedContent}</p>
          </motion.div>
        )}

        {/* Loading state */}
        <AnimatePresence>
          {loading && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-gray-100 p-10 text-center my-8"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}
                className="w-16 h-16 rounded-full bg-gray-900 text-white flex items-center justify-center mx-auto mb-5"
              >
                <Sparkles size={28} />
              </motion.div>
              <p className="text-lg font-bold text-gray-900">Understanding this...</p>
              <p className="text-sm text-gray-400 mt-2">AI is breaking it down into 3 cards</p>
              <div className="flex justify-center gap-1.5 mt-4">
                {[0, 1, 2].map(i => (
                  <motion.div
                    key={i}
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ repeat: Infinity, duration: 1.2, delay: i * 0.3 }}
                    className="w-2 h-2 rounded-full bg-gray-400"
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error state */}
        <AnimatePresence>
          {error && !loading && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              onClick={handleRetry}
              data-testid="share-retry-btn"
              className="w-full bg-red-50 border border-red-100 rounded-2xl p-6 text-center my-4"
            >
              <RefreshCw size={24} className="text-red-400 mx-auto mb-3" />
              <p className="text-sm font-semibold text-red-600">{error}</p>
              <p className="text-xs text-red-400 mt-1">Tap to try again</p>
            </motion.button>
          )}
        </AnimatePresence>

        {/* Explanation cards */}
        <AnimatePresence>
          {explanation && !loading && cardData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Card tabs */}
              <div className="flex gap-2 mb-4">
                {CARD_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    data-testid={`share-tab-${tab.key}`}
                    onClick={() => setActiveCard(tab.key)}
                    className={`flex-1 py-3 rounded-xl text-xs font-extrabold tracking-wider transition-all
                      ${activeCard === tab.key
                        ? 'text-white shadow-md'
                        : 'bg-white border border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    style={activeCard === tab.key ? {
                      background: tab.color,
                      boxShadow: `0 4px 14px ${tab.color}40`,
                    } : {}}
                  >
                    <span className="mr-1">{tab.emoji}</span> {tab.label}
                  </button>
                ))}
              </div>

              {/* Card content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCard}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="rounded-3xl p-6 min-h-[280px] border"
                  style={{
                    background: CARD_TABS.find(t => t.key === activeCard)?.bg,
                    borderColor: CARD_TABS.find(t => t.key === activeCard)?.border,
                  }}
                >
                  {activeCard === 'you' && (cardData.you.affects.length > 0 || cardData.you.action.length > 0) ? (
                    <div className="flex flex-col gap-5">
                      {/* How it affects you */}
                      <div data-testid="share-affects-section">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider mb-3"
                          style={{ background: '#FEE2E2', color: '#DC2626' }}>
                          <AlertTriangle size={10} /> HOW IT AFFECTS YOU
                        </span>
                        <ul className="space-y-2">
                          {cardData.you.affects.map((point, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-800 leading-relaxed">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="border-t border-gray-200/60" />
                      {/* What you should do */}
                      <div data-testid="share-action-section">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider mb-3"
                          style={{ background: '#DBEAFE', color: '#2563EB' }}>
                          <Zap size={10} /> WHAT YOU SHOULD DO
                        </span>
                        <ul className="space-y-2">
                          {cardData.you.action.map((point, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-800 leading-relaxed">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider mb-4"
                        style={{
                          background: CARD_TABS.find(t => t.key === activeCard)?.color + '20',
                          color: CARD_TABS.find(t => t.key === activeCard)?.color,
                        }}
                      >
                        {CARD_TABS.find(t => t.key === activeCard)?.emoji} {cardData[activeCard]?.title}
                      </span>
                      <p className="text-xl font-bold text-gray-900 leading-snug mb-4">
                        {cardData[activeCard]?.content}
                      </p>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {cardData[activeCard]?.detail}
                      </p>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Swipe hint */}
              <p className="text-center text-xs text-gray-400 mt-4">
                Tap tabs to switch cards
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty state: no content and not loading */}
        {!sharedContent && !loading && !explanation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center py-10"
          >
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Share2 size={28} className="text-gray-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Share anything here</h2>
            <p className="text-sm text-gray-400 max-w-xs mx-auto leading-relaxed">
              Share a post from any app, or paste a link above. We'll explain it in 3 simple cards.
            </p>
            <div className="flex flex-col gap-2 mt-8 max-w-xs mx-auto">
              <p className="text-[10px] font-bold tracking-widest text-gray-400 uppercase mb-1">How it works</p>
              {[
                { step: '1', text: 'Share a post from Instagram, X, TikTok...' },
                { step: '2', text: 'Choose WTFHappened from your share sheet' },
                { step: '3', text: 'Get 3 cards: What, Why, and You' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-gray-100 text-left">
                  <span className="w-7 h-7 rounded-full bg-gray-900 text-white text-xs font-bold flex items-center justify-center shrink-0">
                    {item.step}
                  </span>
                  <span className="text-sm text-gray-600">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
